// The Daily reminder on screen: a one-time offer under a finished game's
// result, and the switch in Profile. What happens on a yes or a no, and when
// anything fires, is lib/dailyReminder.ts; these only ask and report.

import { useEffect, useState } from 'react';
import {
  disableReminder, enableReminder, readReminderPref, reminderHourLabel,
  shouldOfferReminder, type ReminderState,
} from '../lib/dailyReminder';
import { logAppEvent } from '../lib/appEvents';

const TEAL = '#14B8A6';
const card = { background: '#182541', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 14 } as const;
const label = {
  fontFamily: 'var(--font-mono)', fontSize: '0.75rem', letterSpacing: '0.2em', color: '#B6B6C2',
} as const;
const button = {
  flex: 1, height: 46, borderRadius: 10, cursor: 'pointer',
  fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: '0.85rem', letterSpacing: '0.12em',
} as const;

/** What a settled answer says, in one line. */
function settledLine(state: ReminderState, hour: number): string | null {
  if (state === 'on') return `Reminder set for around ${reminderHourLabel(hour)}. Turn it off in Profile.`;
  if (state === 'denied') return 'Notifications are off for WOT in Android settings.';
  return null;
}

/**
 * Asked once per install, on the result screen, until it is answered. It
 * sits under the actions so PLAY AGAIN stays the one big button.
 */
export function ReminderOffer() {
  // Decided at mount: answering must not make the card vanish mid-tap; it
  // turns into a one-line confirmation instead.
  const [offer] = useState(() => shouldOfferReminder());
  const [answer, setAnswer] = useState<ReminderState | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (offer) logAppEvent('reminder_prompt'); }, [offer]);

  if (!offer) return null;

  if (answer) {
    const line = settledLine(answer, readReminderPref()?.hour ?? 18);
    if (!line) return null;
    return (
      <div role="status" className="animate-in" style={{ textAlign: 'center', fontSize: '0.85rem', color: answer === 'on' ? TEAL : '#B6B6C2', padding: '2px 8px' }}>
        {line}
      </div>
    );
  }

  const yes = async () => {
    setBusy(true);
    setAnswer(await enableReminder('results'));
    setBusy(false);
  };
  const no = async () => {
    setBusy(true);
    await disableReminder('results');
    setAnswer('off');
    setBusy(false);
  };

  return (
    <div className="animate-in" style={{ ...card, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10, animationDelay: '0.2s' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={label}>DAILY REMINDER</span>
        <span style={{ fontSize: '0.95rem', fontWeight: 700, color: '#ECECF2' }}>
          A nudge when tomorrow's Daily is out?
        </span>
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          onClick={yes}
          disabled={busy}
          style={{ ...button, background: `${TEAL}1F`, border: `1.5px solid ${TEAL}`, color: TEAL, opacity: busy ? 0.6 : 1 }}
        >
          REMIND ME
        </button>
        <button
          onClick={no}
          disabled={busy}
          style={{ ...button, background: '#16223A', border: '1px solid rgba(255,255,255,0.2)', color: '#C4C4D2', opacity: busy ? 0.6 : 1 }}
        >
          NOT NOW
        </button>
      </div>
    </div>
  );
}

/** The Profile row: the same reminder, on or off at any time. */
export function ReminderToggle() {
  const [pref, setPref] = useState(() => readReminderPref());
  const [busy, setBusy] = useState(false);
  const on = pref?.state === 'on';

  const toggle = async () => {
    setBusy(true);
    if (on) await disableReminder('profile');
    else await enableReminder('profile');
    setPref(readReminderPref());
    setBusy(false);
  };

  const status = on
    ? `Around ${reminderHourLabel(pref!.hour)}, on days the Daily is unplayed`
    : pref?.state === 'denied'
      ? 'Allow notifications for WOT in Android settings'
      : 'Off';

  return (
    <button
      onClick={toggle}
      disabled={busy}
      aria-pressed={on}
      style={{
        ...card, width: '100%', minHeight: 56, padding: '8px 14px', marginBottom: 12,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
        cursor: 'pointer', opacity: busy ? 0.6 : 1, textAlign: 'left',
      }}
    >
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={label}>DAILY REMINDER</span>
        <span style={{ fontSize: '0.85rem', color: on ? TEAL : '#B6B6C2' }}>{status}</span>
      </span>
      <span style={{
        flexShrink: 0, fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: '0.8rem', letterSpacing: '0.12em',
        padding: '8px 14px', borderRadius: 999,
        background: on ? TEAL : '#16223A', color: on ? '#0A1222' : '#C4C4D2',
        border: on ? 'none' : '1px solid rgba(255,255,255,0.2)',
      }}>
        {on ? 'ON' : 'TURN ON'}
      </span>
    </button>
  );
}
